import React, { useRef, useState } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import { UploadCloud, Trash2, Star, Link2, GripVertical } from "lucide-react";

// Client-side resize to keep base64 payload small
const resizeImage = (file, maxSize = 1400, quality = 0.82) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxSize) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        } else if (height > maxSize) {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const GalleryEditor = ({ images = [], onChange, primary, setPrimary }) => {
  const { toast } = useToast();
  const [urlInput, setUrlInput] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [processing, setProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setProcessing(true);
    const added = [];
    for (const file of Array.from(files)) {
      if (!file.type.startsWith("image/")) continue;
      try {
        const dataUrl = await resizeImage(file);
        added.push(dataUrl);
      } catch (e) {
        toast({ title: "Skipped a file", description: file.name });
      }
    }
    if (added.length) {
      const next = [...images, ...added];
      onChange(next);
      if (!primary && next[0]) setPrimary(next[0]);
    }
    setProcessing(false);
  };

  const addUrl = () => {
    const v = urlInput.trim();
    if (!v) return;
    if (images.includes(v)) {
      toast({ title: "Already added" });
      setUrlInput("");
      return;
    }
    const next = [...images, v];
    onChange(next);
    if (!primary) setPrimary(v);
    setUrlInput("");
  };

  const remove = (idx) => {
    const removed = images[idx];
    const next = images.filter((_, i) => i !== idx);
    onChange(next);
    if (primary === removed) {
      setPrimary(next[0] || "");
    }
  };

  const makePrimary = (url) => setPrimary(url);

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= images.length) return;
    const next = [...images];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${dragOver ? "border-[#c8102e] bg-[#c8102e]/5" : "border-neutral-300 hover:border-neutral-500 bg-neutral-50"}`}
      >
        <UploadCloud className="mx-auto text-neutral-400 mb-2" size={26} />
        <div className="text-sm text-neutral-700">
          <span className="font-medium text-[#c8102e]">Click to upload</span> or drag & drop images here
        </div>
        <div className="text-xs text-neutral-500 mt-1">JPG / PNG · up to 5 files at a time · images are compressed to 1400px</div>
        {processing && <div className="text-xs text-[#c8102e] mt-2">Processing images…</div>}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      <div className="flex items-center gap-2">
        <div className="flex-1 flex items-center gap-2 border border-neutral-200 px-3">
          <Link2 size={14} className="text-neutral-500" />
          <input
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="Paste image URL and press Add"
            className="flex-1 py-2 outline-none text-sm bg-transparent"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(); } }}
          />
        </div>
        <Button type="button" variant="outline" onClick={addUrl} className="rounded-none">Add URL</Button>
      </div>

      {images.length > 0 && (
        <>
          <div className="text-xs text-neutral-500 uppercase tracking-widest">
            {images.length} image{images.length > 1 ? "s" : ""} in gallery · click a star to set as cover
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {images.map((src, idx) => (
              <div key={src.slice(0, 40) + idx} className="relative group border border-neutral-200 bg-white overflow-hidden">
                <div className="aspect-[4/3] bg-neutral-100">
                  <img src={src} alt={`gallery-${idx}`} className="w-full h-full object-cover" />
                </div>
                {primary === src && (
                  <span className="absolute top-2 left-2 bg-[#c8102e] text-white text-[9px] uppercase tracking-widest px-2 py-1">Cover</span>
                )}
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between px-2 py-1.5 bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="flex items-center gap-1">
                    <button type="button" title="Move left" onClick={() => move(idx, -1)} className="p-1 hover:bg-white/10"><GripVertical size={12} /></button>
                    <button type="button" title="Set as cover" onClick={() => makePrimary(src)} className="p-1 hover:bg-white/10"><Star size={12} className={primary === src ? "fill-amber-400 text-amber-400" : ""} /></button>
                  </div>
                  <button type="button" title="Remove" onClick={() => remove(idx)} className="p-1 hover:bg-white/10"><Trash2 size={12} /></button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default GalleryEditor;
